import cloneDeep from "lodash/cloneDeep";
import type { OpenSearchCursor, OpenSearchQuery, Pagination } from "@/types";
import { firstPageQuery, lastPageQuery } from "@/constants/general.ts";

/**
 * Determines the correct OpenSearch cursor query based on pagination direction.
 * @pure
 * @param newPagination - The new pagination state being navigated to
 * @param oldPagination - The previous pagination state
 * @param pages - Total number of pages
 * @param cursor - Current OpenSearch cursor with prev/next page tokens
 * @returns The appropriate OpenSearchQuery for the requested page
 */
export const getOpenSearchQuery = (
  newPagination: Pagination,
  oldPagination: Pagination,
  pages: number,
  cursor: OpenSearchCursor
): OpenSearchQuery => {
  let openSearchQuery: OpenSearchQuery = firstPageQuery;
  if (pages > 1) {
    if (newPagination.page === pages - 1) {
      openSearchQuery = lastPageQuery;
    } else if (newPagination.page === 0) {
      openSearchQuery = firstPageQuery;
    } else if (newPagination.page > oldPagination.page) {
      openSearchQuery = cursor.nextPage!;
    } else if (newPagination.page < oldPagination.page) {
      openSearchQuery = cursor.prevPage!;
    }
  }
  return openSearchQuery;
};

/**
 * Serializes an OpenSearch query object — joins array values into comma-separated strings.
 * @pure
 * @param obj - The OpenSearchQuery to serialize
 * @returns A new OpenSearchQuery with array values joined as strings
 */
export const parseOpenSearchQuery = (obj: OpenSearchQuery): OpenSearchQuery => {
  const result: OpenSearchQuery = cloneDeep({ ...obj });

  for (const key in result) {
    if (Array.isArray(result[key])) {
      result[key] = result[key].join(",");
    }
  }

  return result;
};
